import styled from 'styled-components';
import selectOnFocus from '../../../../../../modules/autoselect';
import PlusIcon from '../../../../../../generic/icons/IconLib/PlusIcon';
import MinusIcon from '../../../../../../generic/icons/IconLib/MinusIcon';

const Article = styled.article`
    position: relative;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 18px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    font-family: ${({ theme }) => theme.fonts.sans};
    color: ${({ theme }) => theme.colors.ink};
    transition:
        border-color ${({ theme }) => theme.motion.fast},
        box-shadow ${({ theme }) => theme.motion.base},
        transform ${({ theme }) => theme.motion.base};

    &:hover {
        border-color: #DCDCD7;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02), 0 12px 28px -16px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }
`;

function ProductCard({ children, ...rest }) {
    return <Article {...rest}>{children}</Article>;
}

const MoreBtn = styled.button`
    position: absolute;
    top: 8px;
    inset-inline-start: 8px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(6px);
    color: ${({ theme }) => theme.colors.ink2};
    display: grid;
    place-items: center;
    cursor: pointer;
    z-index: 4;
    transition:
        background ${({ theme }) => theme.motion.fast},
        color ${({ theme }) => theme.motion.fast},
        border-color ${({ theme }) => theme.motion.fast};

    &:hover {
        background: #fff;
        color: ${({ theme }) => theme.colors.ink};
        border-color: #DCDCD7;
    }

    svg { width: 16px; height: 16px; }
`;

ProductCard.MoreButton = function MoreButton({ onClick, ...rest }) {
    const handle = (e) => {
        e.stopPropagation();
        onClick?.(e);
    };
    return (
        <MoreBtn type="button" aria-label="עוד" onClick={handle} {...rest}>
            <svg viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.6" />
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="12" cy="19" r="1.6" />
            </svg>
        </MoreBtn>
    );
};

const QtyPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px 4px 7px;
    background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.line2)};
    color: ${({ $active, theme }) => ($active ? theme.colors.ink : theme.colors.ink3)};
    border-radius: ${({ theme }) => theme.radii.pill};
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 10.5px;
    font-weight: 500;
    line-height: 1;
    transition:
        background ${({ theme }) => theme.motion.base},
        color ${({ theme }) => theme.motion.base};

    .ico {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .ico > * { font-size: 10.5px; line-height: 1; }
`;

ProductCard.QtyBadge = function QtyBadge({ value, icon, active }) {
    return (
        <QtyPill $active={!!active}>
            {icon ? <span className="ico">{icon}</span> : null}
            <span>{value ?? 0}</span>
        </QtyPill>
    );
};

const MediaBox = styled.div`
    position: relative;
    aspect-ratio: 5 / 4;
    background: #fff;
    border-radius: 12px;
    display: grid;
    place-items: center;
    overflow: hidden;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    img {
        width: 78%;
        height: 78%;
        object-fit: contain;
        transform: scale(1.3);
        transform-origin: center;
    }
    .placeholder {
        width: 64%;
        height: 64%;
        color: ${({ theme }) => theme.colors.ink4};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        aspect-ratio: 1 / 1;
        img {
            transform: none;
        }
    }
`;

ProductCard.Media = function Media({ children, onClick }) {
    return (
        <MediaBox $clickable={!!onClick} onClick={onClick}>
            {children}
        </MediaBox>
    );
};

const TitleSlot = styled.div`
    /*
     * Fixed height so every card in the grid has the same vertical size,
     * regardless of whether the product name fits on one line, wraps to
     * two lines, or comes with a hint badge underneath. Tall enough for
     * 2 lines of TitleText (font 14.5px × line-height 1.25 ≈ 36px) plus
     * a single-line HintRow (~13px) with the 2px gap between them.
     */
    height: 3.8em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 0 2px;
    overflow: hidden;
    text-align: start;
`;

const TitleText = styled.h3`
    margin: 0;
    font-size: 14.5px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    line-height: 1.25;
    letter-spacing: -0.005em;
    text-align: start;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    /*
     * Cap at 2 lines so an unusually long name doesn't overflow the slot
     * and break the grid; the slot height above is sized for exactly 2.
     */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const HintRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.ink3};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: start;
`;

ProductCard.TitleBlock = function TitleBlock({ title, hint }) {
    return (
        <TitleSlot>
            <TitleText $clamp={hint ? 1 : 2}>{title}</TitleText>
            {hint ? <HintRow>{hint}</HintRow> : null}
        </TitleSlot>
    );
};

const HintPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px 4px 7px;
    background: #FCE4EC;
    color: #8B2548;
    border-radius: ${({ theme }) => theme.radii.pill};
    font-size: 10.5px;
    font-weight: 500;
    line-height: 1;

    b {
        font-family: ${({ theme }) => theme.fonts.mono};
        font-size: 10.5px;
        font-weight: 600;
    }
    svg {
        width: 10px;
        height: 10px;
        color: #B83A66;
        flex-shrink: 0;
    }
`;

const HintBadgeEl = styled(HintPill)`
    position: absolute;
    top: 8px;
    inset-inline-end: 8px;
    z-index: 4;
`;

const HintStar = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" />
    </svg>
);

ProductCard.HintBadge = function HintBadge({ label = 'המלצה', value }) {
    return (
        <HintBadgeEl>
            <HintStar />
            {label} · <b>{value}</b>
        </HintBadgeEl>
    );
};

ProductCard.HintInlineBadge = function HintInlineBadge({ value }) {
    return (
        <HintPill>
            <HintStar />
            <b>{value}</b>
        </HintPill>
    );
};

const PackSelectEl = styled.select`
    appearance: none;
    -webkit-appearance: none;
    width: 100%;
    background: ${({ theme }) => theme.colors.bg};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 10px;
    padding: 8px 10px;
    padding-inline-start: 24px;
    font: inherit;
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.ink2};
    cursor: pointer;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23757575' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: left 8px center;
    direction: rtl;
    transition:
        border-color ${({ theme }) => theme.motion.fast},
        background-color ${({ theme }) => theme.motion.fast};

    &:hover {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
        background-color: #fff;
    }
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

ProductCard.PackSelect = PackSelectEl;

const StepperWrap = styled.div`
    display: flex;
    align-items: center;
    background: ${({ $accent, theme }) => ($accent ? theme.colors.accent : theme.colors.bg)};
    border: 1px solid ${({ $accent, theme }) => ($accent ? theme.colors.accent : theme.colors.inputBorder)};
    border-radius: 10px;
    overflow: hidden;
    height: 38px;
    transition:
        background ${({ theme }) => theme.motion.fast},
        border-color ${({ theme }) => theme.motion.fast};

    button {
        width: 32px;
        flex: 0 0 32px;
        height: 100%;
        border: 0;
        background: transparent;
        color: ${({ theme }) => theme.colors.ink};
        cursor: pointer;
        display: grid;
        place-items: center;
        transition: background ${({ theme }) => theme.motion.fast};
    }
    button:hover:not(:disabled) {
        background: ${({ $accent, theme }) => ($accent ? theme.colors.accentDeep : theme.colors.line2)};
    }
    button:disabled {
        color: ${({ theme }) => theme.colors.ink4};
        cursor: not-allowed;
    }
    .plus { order: 0; }
    input { order: 1; }
    .minus { order: 2; }

    input {
        flex: 1;
        min-width: 0;
        border: 0;
        background: transparent;
        outline: none;
        text-align: center;
        font: inherit;
        font-family: ${({ theme }) => theme.fonts.mono};
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.ink};
        -moz-appearance: textfield;
        appearance: textfield;
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const ReadonlyPlate = styled.div`
    display: grid;
    place-items: center;
    height: 38px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 13px;
    line-height: 1.1;
    text-align: center;
    padding: 0 8px;
`;

ProductCard.Stepper = function Stepper({
    value,
    min = 0,
    disabled,
    accent,
    onPlus,
    onMinus,
    onInputChange,
    mode = 'editable',
    readonlyContent,
}) {
    if (mode === 'readonly') {
        return <ReadonlyPlate onClick={(e) => e.stopPropagation()}>{readonlyContent}</ReadonlyPlate>;
    }
    const stop = (fn) => (e) => {
        e.stopPropagation();
        fn?.(e);
    };
    return (
        <StepperWrap $accent={!!accent} onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                className="plus"
                aria-label="more"
                onClick={stop(onPlus)}
                disabled={disabled}
            >
                <PlusIcon size={12} color='currentColor' />
            </button>
            <input
                type="number"
                inputMode="numeric"
                value={value}
                min={min}
                disabled={disabled}
                onChange={onInputChange}
                onWheel={(e) => e.currentTarget.blur()}
                onClick={(e) => {
                    e.stopPropagation();
                    selectOnFocus(e);
                } }
            />
            <button
                type="button"
                className="minus"
                aria-label="less"
                onClick={stop(onMinus)}
                disabled={disabled || (Number(value) || 0) <= min}
            >
                <MinusIcon size={12} color='currentColor' />
            </button>
        </StepperWrap>
    );
};

const PanelEl = styled.div`
    position: absolute;
    inset: 0;
    background: #fff;
    border-radius: 18px;
    padding: 14px;
    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(101%)')};
    transition: transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
    z-index: 6;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: 1px solid ${({ theme }) => theme.colors.line};
`;

const PanelHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};

    h3 {
        margin: 0;
        font-size: 13px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.ink};
        letter-spacing: -0.01em;
    }
`;

const CloseBtn = styled.button`
    width: 26px;
    height: 26px;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.ink3};
    cursor: pointer;
    border-radius: 6px;
    display: grid;
    place-items: center;
    transition:
        background ${({ theme }) => theme.motion.fast},
        color ${({ theme }) => theme.motion.fast};

    &:hover {
        background: ${({ theme }) => theme.colors.line2};
        color: ${({ theme }) => theme.colors.ink};
    }
    svg { width: 14px; height: 14px; }
`;

ProductCard.Panel = function Panel({ open, onClose, title = 'פרטים נוספים', children }) {
    const handleClose = (e) => {
        e.stopPropagation();
        onClose?.(e);
    };
    return (
        <PanelEl $open={!!open} onClick={(e) => e.stopPropagation()}>
            <PanelHead>
                <h3>{title}</h3>
                <CloseBtn type="button" aria-label="close" onClick={handleClose}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </CloseBtn>
            </PanelHead>
            {children}
        </PanelEl>
    );
};

const FieldGridEl = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
    }
`;

ProductCard.FieldGrid = FieldGridEl;

const FieldRow = styled.div`
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
    flex-direction: column;
    gap: 5px;

    label {
        font-size: 11px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.ink2};
    }
    input,
    textarea {
        width: 100%;
        background: ${({ theme }) => theme.colors.bg};
        border: 1px solid ${({ theme }) => theme.colors.inputBorder};
        border-radius: 8px;
        padding: 8px 10px;
        font: inherit;
        font-size: 13px;
        color: ${({ theme }) => theme.colors.ink};
        outline: none;
        direction: rtl;
        transition:
            border-color ${({ theme }) => theme.motion.fast},
            box-shadow ${({ theme }) => theme.motion.fast};
        font-family: ${({ theme }) => theme.fonts.mono};
        resize: none;
    }
    textarea {
        font-family: ${({ theme }) => theme.fonts.sans};
        min-height: 64px;
    }
    input:focus,
    textarea:focus {
        border-color: ${({ theme }) => theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }
    input:disabled,
    textarea:disabled {
        opacity: 0.6;
    }
`;

const FieldShell = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: ${({ $hasAction }) => ($hasAction ? 'auto 1fr' : '1fr')};
    align-items: center;
    background: ${({ theme }) => theme.colors.bg};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 8px;
    transition:
        border-color ${({ theme }) => theme.motion.fast},
        box-shadow ${({ theme }) => theme.motion.fast};
    direction: rtl;

    &:focus-within {
        border-color: ${({ theme }) => theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    > input {
        background: transparent;
        border: 0;
        outline: 0;
        padding: 8px 10px;
        font: inherit;
        font-size: 13px;
        color: ${({ theme }) => theme.colors.ink};
        font-family: ${({ theme }) => theme.fonts.mono};
        direction: rtl;
        width: 100%;
        min-width: 0;
    }
    > input:disabled { opacity: 0.6; }
`;

const FieldAction = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-inline-end: 4px;
    border: 0;
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink2};
    cursor: pointer;
    transition:
        background ${({ theme }) => theme.motion.fast},
        color ${({ theme }) => theme.motion.fast};

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
    }
    svg { width: 11px; height: 11px; }
    svg { width: 12px; height: 12px; }
`;

ProductCard.Field = function Field({ label, actionIcon, onAction, hidden, ...inputProps }) {
    const hasAction = !!actionIcon && !!onAction;
    return (
        <FieldRow $hidden={hidden}>
            {label ? <label>{label}</label> : null}
            {hasAction ? (
                <FieldShell $hasAction>
                    <FieldAction
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onAction(e); }}
                        aria-label="action"
                    >
                        {actionIcon}
                    </FieldAction>
                    <input {...inputProps} />
                </FieldShell>
            ) : (
                <input {...inputProps} />
            )}
        </FieldRow>
    );
};

const Counter = styled.div`
    text-align: start;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 10.5px;
    color: ${({ $warn, theme }) => ($warn ? theme.colors.danger : theme.colors.ink3)};
    margin-top: 2px;
`;

ProductCard.Textarea = function Textarea({ label, value = '', maxLength, onChange, hidden, ...rest }) {
    const left = typeof maxLength === 'number' ? maxLength - String(value).length : null;
    return (
        <FieldRow $hidden={hidden}>
            {label ? <label>{label}</label> : null}
            <textarea value={value} maxLength={maxLength} onChange={onChange} {...rest} />
            {typeof left === 'number' ? (
                <Counter $warn={left <= 20}>נשארו {left} תווים</Counter>
            ) : null}
        </FieldRow>
    );
};

export default ProductCard;
